import { useEffect, useState, useCallback } from 'react'
import { useSupabase } from '@/hooks/use-supabase'
import { useAuthStore } from '@/store/auth-store'

export function useProfile() {
  const { user } = useAuthStore()
  const [profileExists, setProfileExists] = useState(false)
  const [loading, setLoading] = useState(true)
  const [homeCurrency, setHomeCurrency] = useState<string | null>(null)
  const [avatarColor, setAvatarColor] = useState<string>('bg-teal-500')
  const supabase = useSupabase()

  const getUserInitials = (name?: string) => {
    if (!name) return 'U'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const ensureDefaultCategories = useCallback(async () => {
    if (!user) return

    try {
      // Verificar si ya tiene categorías
      const { data: existingCategories } = await supabase
        .from('categories')
        .select('id')
        .eq('user_id', user.id)
        .limit(1)

      if (existingCategories && existingCategories.length > 0) {
        return // Ya tiene categorías
      }

      // Crear categorías por defecto
      const defaultCategories = [
        // Gastos
        { name: 'Alimentación', color: '#10B981', icon: 'ShoppingCart', type: 'expense' },
        { name: 'Vivienda', color: '#3B82F6', icon: 'Home', type: 'expense' },
        { name: 'Transporte', color: '#F59E0B', icon: 'Bus', type: 'expense' },
        { name: 'Servicios', color: '#8B5CF6', icon: 'Zap', type: 'expense' },
        { name: 'Salud', color: '#EF4444', icon: 'HeartPulse', type: 'expense' },
        { name: 'Entretenimiento', color: '#EC4899', icon: 'Film', type: 'expense' },
        { name: 'Deudas y Préstamos', color: '#7E22CE', icon: 'Landmark', type: 'expense' },
        { name: 'Educación', color: '#06B6D4', icon: 'BookOpen', type: 'expense' },
        { name: 'Comunicación', color: '#059669', icon: 'Phone', type: 'expense' },
        { name: 'Seguridad Social', color: '#D97706', icon: 'Users', type: 'expense' },
        { name: 'Seguros', color: '#6366F1', icon: 'ShieldCheck', type: 'expense' },
        { name: 'General', color: '#6B7280', icon: 'Folder', type: 'expense' },
        // Ingresos
        { name: 'Salario', color: '#10B981', icon: 'Landmark', type: 'income' },
        { name: 'Freelance', color: '#3B82F6', icon: 'Laptop', type: 'income' },
        { name: 'Asesoría', color: '#8B5CF6', icon: 'Users', type: 'income' },
        { name: 'Consulta', color: '#F59E0B', icon: 'MessageSquare', type: 'income' },
        { name: 'Clases', color: '#EF4444', icon: 'BookOpen', type: 'income' },
        { name: 'Ventas', color: '#EC4899', icon: 'ShoppingBag', type: 'income' },
        { name: 'Arriendo', color: '#06B6D4', icon: 'Home', type: 'income' },
        { name: 'Rendimientos', color: '#059669', icon: 'TrendingUp', type: 'income' },
        { name: 'Otros Ingresos', color: '#6B7280', icon: 'DollarSign', type: 'income' },
      ]

      const categoriesToInsert = defaultCategories.map(cat => ({
        ...cat,
        user_id: user.id,
        is_default: true // Mark as system default category
      }))

      await supabase.from('categories').insert(categoriesToInsert)
    } catch (err) {
      console.error('Error creating default categories:', err)
    }
  }, [user, supabase])

  const createProfile = useCallback(async (currency: string = 'COP') => {
    if (!user) return

    try {
      // Crear perfil
      const { error: profileError } = await supabase.from('profiles').insert({
        id: user.id,
        email: user.email!,
        full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuario',
        home_currency: currency,
      })
      if (profileError) throw profileError
      setProfileExists(true)
      setHomeCurrency('COP')
      await ensureDefaultCategories()
    } catch (err) {
      console.error('Error in createProfile:', err)
    }
  }, [user, supabase, ensureDefaultCategories])

  const checkAndCreateProfile = useCallback(async () => {
    if (!user) return

    try {
      // Verificar si el perfil existe
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*, home_currency')
        .eq('id', user.id)
        .single()

      if (error && error.code === 'PGRST116') {
        // El perfil no existe, crearlo
        await createProfile()
      } else if (profile) {
        setProfileExists(true)
        setHomeCurrency(profile.home_currency)
        setAvatarColor(profile.avatar_url || 'avatar1')
        await ensureDefaultCategories()
      }
    } catch (err) {
      console.error('Error checking profile:', err)
    } finally {
      setLoading(false)
    }
  }, [user, supabase, createProfile, ensureDefaultCategories])

  useEffect(() => {
    if (!user) {
      setLoading(false)
      return
    }

    checkAndCreateProfile()
  }, [user, checkAndCreateProfile])

  return { profileExists, loading, checkAndCreateProfile, homeCurrency, avatarColor, getUserInitials }
}
